import { useEffect, useState } from 'react'

import { getOrganizationClient } from '@/api'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/use-toast'
import { IOrganization } from '@/models'

export interface RetrySetupOwner {
  full_name?: string
  email_address: string
}

interface RetrySetupDialogProps {
  organization?: IOrganization
  /** Prefills the owner, e.g. with what was entered when the organization was created */
  defaultOwner?: RetrySetupOwner
  open: boolean
  onOpenChange: (open: boolean) => void
  onSetupComplete: () => void
}

// TODO: switch to the typed `retryOrganizationSetup` operation once @epilot/organization-client-internal ships it
const retryOrganizationSetup = (orgId: string, owner: RetrySetupOwner) =>
  getOrganizationClient().post(
    `/v2/internal/organizations/${orgId}/retry-setup`,
    { owner_user: owner }
  )

export function RetrySetupDialog({
  organization,
  defaultOwner,
  open,
  onOpenChange,
  onSetupComplete
}: RetrySetupDialogProps) {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (open) {
      setEmail(defaultOwner?.email_address ?? organization?.email ?? '')
      setFullName(defaultOwner?.full_name ?? '')
    }
  }, [open, defaultOwner, organization?.email])

  const handleRetry = async () => {
    if (!organization?.id) return

    setIsLoading(true)

    try {
      await retryOrganizationSetup(String(organization.id), {
        email_address: email.trim(),
        full_name: fullName.trim() || undefined
      })

      toast({
        title: 'Setup finished',
        description: `The login is set up and an invitation was sent to ${email.trim()}.`
      })

      onSetupComplete()
      onOpenChange(false)
    } catch (err) {
      const error = err as {
        response?: { data?: { error?: string; message?: string } }
        message?: string
      }

      toast({
        title: 'Setup not finished yet',
        description:
          error?.response?.data?.error ??
          error?.response?.data?.message ??
          error?.message ??
          'An unexpected error occurred.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-primary">
            {`Finish setting up "${organization?.name ?? ''}"`}
          </DialogTitle>
          <DialogDescription>
            {
              "The login for this organization wasn't ready when it was created, so its owner hasn't been invited yet. This finishes the setup and sends the invitation. It can take up to 20 seconds."
            }
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="retry-setup-owner-email">Owner email</Label>
            <Input
              id="retry-setup-owner-email"
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              value={email}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="retry-setup-owner-name">Owner name</Label>
            <Input
              id="retry-setup-owner-name"
              onChange={(e) => setFullName(e.target.value)}
              value={fullName}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            disabled={isLoading}
            onClick={() => onOpenChange(false)}
            variant="outline"
          >
            Cancel
          </Button>
          <Button disabled={isLoading || !email.trim()} onClick={handleRetry}>
            {isLoading ? 'Finishing setup…' : 'Retry setup'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
